import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Filter, MapPin, Plus, Search } from 'lucide-react';
import {
  BosDurum,
  Buton,
  Girdi,
  Rozet,
  VeriTablo,
  type ColumnDef,
  type RozetTonu,
} from '@emanetx/ui';
import type { Nokta, NoktaDurumu, ParaBirimi } from '@emanetx/sdk';
import { useNoktaListesi } from './api.js';
import { YeniNoktaDialog } from './yeni-nokta-dialog.js';

const DURUM_TONU: Record<NoktaDurumu, RozetTonu> = {
  KURULUM: 'turuncu',
  AKTIF: 'yesil',
  BAKIM: 'turuncu',
  PASIF: 'gri',
};

const DURUM_ETIKET: Record<NoktaDurumu, string> = {
  KURULUM: 'Kurulum',
  AKTIF: 'Aktif',
  BAKIM: 'Bakım',
  PASIF: 'Pasif',
};

const PARA_BIRIMI_SEMBOL: Record<ParaBirimi, string> = {
  TRY: '₺',
  EUR: '€',
  USD: '$',
};

export function SayfaNoktalar() {
  const [sayfa, setSayfa] = useState(1);
  const [arama, setArama] = useState('');
  const [durum, setDurum] = useState<NoktaDurumu | ''>('');
  const [yeniAcik, setYeniAcik] = useState(false);
  const navigate = useNavigate();

  const sorgu = {
    sayfa,
    boyut: 20,
    ...(arama && { arama }),
    ...(durum && { durum: durum as NoktaDurumu }),
  };

  const liste = useNoktaListesi(sorgu);

  const kolonlar = useMemo<ColumnDef<Nokta, unknown>[]>(
    () => [
      {
        accessorKey: 'kod',
        header: 'Kod',
        meta: { etiket: 'Kod' },
        cell: ({ row }) => (
          <Link
            to="/panel/noktalar/$id"
            params={{ id: row.original.id }}
            onClick={(e) => e.stopPropagation()}
            className="text-marka-700 hover:text-marka-800 dark:text-marka-400 font-mono text-xs font-semibold hover:underline"
          >
            {row.original.kod}
          </Link>
        ),
      },
      {
        accessorKey: 'ad',
        header: 'Ad',
        meta: { etiket: 'Ad' },
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.ad}</div>
            {row.original.adres && (
              <div className="text-fume-500 max-w-[260px] truncate text-xs">
                {row.original.adres}
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'grupAdi',
        header: 'Grup',
        meta: { etiket: 'Grup' },
        cell: ({ row }) => (
          <span className="text-fume-600 dark:text-fume-300">{row.original.grupAdi ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={DURUM_TONU[row.original.durum]} nokta>
            {DURUM_ETIKET[row.original.durum]}
          </Rozet>
        ),
      },
      {
        accessorKey: 'paraBirimi',
        header: 'Para',
        meta: { etiket: 'Para' },
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {PARA_BIRIMI_SEMBOL[row.original.paraBirimi]} {row.original.paraBirimi}
          </span>
        ),
      },
      {
        id: 'dolap',
        header: 'Dolap',
        meta: { etiket: 'Dolap' },
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-baseline gap-1">
            <span className="font-mono font-semibold">{row.original.musaitDolapSayisi ?? 0}</span>
            <span className="text-fume-400 text-xs">/ {row.original.dolapSayisi ?? 0}</span>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Noktalar</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            Raspberry Pi'lerin yerleştirildiği fiziksel lokasyonlar.
          </p>
        </div>
        <Buton onClick={() => setYeniAcik(true)}>
          <Plus size={16} /> Yeni Nokta
        </Buton>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={20}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        onSatirTikla={(n) => void navigate({ to: '/panel/noktalar/$id', params: { id: n.id } })}
        bosDurum={
          <BosDurum
            ikon={MapPin}
            baslik={arama || durum ? 'Filtreyle eşleşen nokta yok' : 'Henüz nokta yok'}
            aciklama={
              arama || durum
                ? 'Filtreyi temizleyip tekrar deneyin.'
                : 'İlk noktayı ekleyerek başlayın.'
            }
            aksiyon={
              <Buton onClick={() => setYeniAcik(true)}>
                <Plus size={14} /> Yeni Nokta
              </Buton>
            }
          />
        }
        ustAksiyonlar={
          <>
            <div className="relative min-w-0 flex-1">
              <Search
                size={14}
                className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="Kod, ad, grup veya adres ile ara..."
                value={arama}
                onChange={(e) => {
                  setArama(e.target.value);
                  setSayfa(1);
                }}
                className="h-10 pl-9 text-xs"
              />
            </div>
            <Filter size={13} className="text-fume-400" />
            <select
              value={durum}
              onChange={(e) => {
                setDurum(e.target.value as NoktaDurumu | '');
                setSayfa(1);
              }}
              className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
            >
              <option value="">Tüm durumlar</option>
              <option value="KURULUM">Kurulum</option>
              <option value="AKTIF">Aktif</option>
              <option value="BAKIM">Bakım</option>
              <option value="PASIF">Pasif</option>
            </select>
          </>
        }
      />

      <YeniNoktaDialog acik={yeniAcik} onDegisim={setYeniAcik} />
    </div>
  );
}
